import React, {useEffect, useRef, useState} from "react";
import {WealthyEnv} from "helpers";
import {colors} from "nxtconstants";
import styled from "styled-components";

const TimelineNew = ({timelineData = [], timelineSectionData = {}}) => {
  const {title = "", bottomText = "", buttonText = ""} = timelineSectionData;
  const [currentCard, setCurrentCard] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const carouselRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
          setTimeout(() => {
            setCurrentCard(timelineData.length - 1);
            scrollToCard(timelineData.length - 1);
          }, 500);
        }
      },
      {threshold: 0.3}
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible, timelineData.length]);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setCurrentCard(0);
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile) return;

    const interval = setInterval(() => {
      setCurrentCard((prev) => (prev + 1) % timelineData.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isMobile, timelineData.length]);

  const scrollToCard = (index) => {
    if (carouselRef.current) {
      const cardWidth = carouselRef.current.scrollWidth / timelineData.length;
      carouselRef.current.scrollTo({
        left: index * cardWidth,
        behavior: "smooth"
      });
    }
  };

  const handleCardClick = (index) => {
    setCurrentCard(index);
    scrollToCard(index);
  };

  const handleScroll = () => {
    if (carouselRef.current) {
      const scrollLeft = carouselRef.current.scrollLeft;
      const cardWidth = carouselRef.current.scrollWidth / timelineData.length;
      const newIndex = Math.round(scrollLeft / cardWidth);
      if (newIndex !== currentCard) {
        setCurrentCard(newIndex);
      }
    }
  };

  return (
    <TimelineContainer ref={containerRef}>
      <TimelineTitle>{title}</TimelineTitle>

      {isMobile ? (
        <MobileCarousel>
          <CarouselWrapper>
            <CarouselContainer ref={carouselRef} onScroll={handleScroll}>
              {timelineData.map((item, index) => (
                <TimelineCard
                  key={index}
                  $index={index}
                  $isActive={index === currentCard}
                  onClick={() => handleCardClick(index)}
                  $isMobile
                >
                  <CardContent>
                    <Year>{item.year}</Year>
                    <Milestone>{item.milestone}</Milestone>

                    {item.amount && (
                      <Amount>{item.amount}</Amount>
                    )}

                    <Details $index={index} $isMobile>
                      {Array.isArray(item.details) ? (
                        <DetailsList>
                          {item.details.map((detail, idx) => (
                            <DetailItem key={idx}>• {detail}</DetailItem>
                          ))}
                        </DetailsList>
                      ) : (
                        item.details
                      )}
                    </Details>

                    <VisualElement>
                      {item.hasImage ? (
                        <CardImage
                          src={item.imageUrl}
                          alt={item.milestone}
                          width={640}
                          height={400}
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <VisualText>{item.visualElement}</VisualText>
                      )}
                    </VisualElement>
                  </CardContent>
                </TimelineCard>
              ))}
            </CarouselContainer>
          </CarouselWrapper>
        </MobileCarousel>
      ) : (
        <DesktopCarousel>
          <CarouselWrapper>
            <CarouselContainer ref={carouselRef} onScroll={handleScroll}>
              {timelineData.map((item, index) => (
                <TimelineCard
                  key={index}
                  $index={index}
                  $isActive={index === currentCard}
                  onClick={() => handleCardClick(index)}
                >
                  <CardContent>
                    <Year>{item.year}</Year>
                    <Milestone>{item.milestone}</Milestone>

                    {item.amount && (
                      <Amount>{item.amount}</Amount>
                    )}

                    <Details $index={index}>
                      {Array.isArray(item.details) ? (
                        <DetailsList>
                          {item.details.map((detail, idx) => (
                            <DetailItem key={idx}>• {detail}</DetailItem>
                          ))}
                        </DetailsList>
                      ) : (
                        item.details
                      )}
                    </Details>

                    <VisualElement>
                      {item.hasImage ? (
                        <CardImage
                          src={item.imageUrl}
                          alt={item.milestone}
                          width={640}
                          height={400}
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <VisualText>{item.visualElement}</VisualText>
                      )}
                    </VisualElement>
                  </CardContent>
                </TimelineCard>
              ))}
            </CarouselContainer>
          </CarouselWrapper>

        </DesktopCarousel>
      )}

      <BottomSection>
        <BottomText>
          {bottomText}
        </BottomText>
        <ButtonSection>
          <CTAButton href="#register">
            {buttonText}
          </CTAButton>
        </ButtonSection>
      </BottomSection>
    </TimelineContainer>
  );
};

const TimelineContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  background: #ffffff;
`;

const TimelineTitle = styled.h2`
  font-family: 'DM Serif Display';
  font-size: 5rem;
  font-weight: 400;
  text-align: center;
  margin-bottom: 4rem;
  color: #1D1D1F;

  @media (max-width: 768px) {
    font-size: 2.7rem;
    margin-top: 4rem;
  }
`;

const DesktopCarousel = styled.div`
  margin-bottom: 4rem;
`;

const CarouselWrapper = styled.div`
  position: relative;
  overflow: hidden;
  margin-bottom: 2rem;
`;

const CarouselContainer = styled.div`
  display: flex;
  gap: 2rem;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  align-items: end; 
    width: 90%;
    margin-left: 7.5rem;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    justify-content: flex-start;
    width: 95%;
    margin-left: 1.2rem;
  }
`;

const MobileCarousel = styled.div`
  margin-bottom: 4rem;
`;

const TimelineCard = styled.div`
  background: #F9F6FF;
  border-radius: 16px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  min-height: ${props => {
    if (props.$isMobile) {
      if (props.$index === 0) return "300px";
      if (props.$index === 1) return "340px";
      if (props.$index === 2) return "380px";
      if (props.$index === 3) return "420px";
      if (props.$index === 4) return "460px";
      return "400px";
    }

    if (props.$index === 0) return "430px";
    if (props.$index === 1) return "490px";
    if (props.$index === 2) return "530px";
    if (props.$index === 3) return "560px";
    if (props.$index === 4) return "600px";

    return "500px";
  }};
  width: ${props => props.$isMobile ? "280px" : "320px"};
  max-width: ${props => props.$isMobile ? "280px" : "none"};
  flex-shrink: 0;
  cursor: pointer;

  @media (max-width: 768px) {
    padding: 1.5rem;
    margin: 0 !important;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-left: 1rem;
`;

const Year = styled.div`
  font-size: 5rem;
  font-weight: 400;
  color: #1C1C1C;
  margin-bottom: 1rem;
  font-family: 'DM Serif Display';

  @media (max-width: 768px) {
    font-size: 2.22rem;
  }
`;

const Milestone = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: #967DCC;
  margin-bottom: 1rem;
  line-height: 1.4;
  font-family: 'Lato';

  @media (max-width: 768px) {
    font-size: 1.77rem;
  }
`;

const Amount = styled.div`
  font-size: 1.8rem;
  font-weight: bold;
  color: #10b981;
  margin-bottom: 1rem;
  font-family: 'Inter', sans-serif;

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const Details = styled.div`
  font-size: 1.6rem;
  color: #6b7280;
  line-height: 1.6;
  font-weight: 400;
  width: 80%;
  flex: 1;
  font-family: 'Lato';
  margin-bottom: ${props => {
    if (props.$isMobile) {
      if (props.$index === 0) return "80px";
      if (props.$index === 1) return "120px";
      if (props.$index === 2) return "45px";
      if (props.$index === 3) return "160px";
      if (props.$index === 4) return "90px";
      return "1.5rem";
    }

    if (props.$index === 0) return "142px";
    if (props.$index === 1) return "217px";
    if (props.$index === 2) return "176px";
    if (props.$index === 3) return "287px";
    if (props.$index === 4) return "215px";

    return "2rem";
  }};

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const DetailsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const DetailItem = styled.li`
  margin-bottom: 0.8rem;
  padding-left: 0;

  &:last-child {
    margin-bottom: 0;
  }
`;

const VisualElement = styled.div`
  margin-top: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 120px;
  padding-bottom: 1rem;
`;

const VisualText = styled.div`
  font-size: 3rem;
  font-weight: bold;
  color: rgba(99, 102, 241, 0.3);
  font-family: 'Inter', sans-serif;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const CardImage = styled.img`
  width: 100%;
  max-width: 200px;
  height: auto;
  border-radius: 8px;
  object-fit: cover;

  @media (max-width: 768px) {
    width: 90%;
    max-width: none;
  }
`;

const BottomSection = styled.div`
  text-align: center;
  padding: 3rem 0 0 0;

  @media(max-width: 768px) {
    display: none;
  }
`;

const BottomText = styled.p`
  font-size: 2rem;
  font-weight: 400;
  color: #6b7280;
  margin-bottom: 2rem;
  font-family: 'Lato';

  @media (max-width: 768px) {
    display: none;
  }
`;

const ButtonSection = styled.div`
    width: fit-content;
    text-align: center;
    margin: 0 auto;
`;

const CTAButton = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${colors.primary};
  color: white;
  border: none;
  border-radius: 5rem;
  padding: 1rem 4rem;
  font-family: 'Lato';
  font-size: 1.8rem;
  font-weight: 700;
  justify-content: center;
  cursor: pointer;
  text-decoration: none;

  @media (max-width: 768px) {
    padding: 1rem 7rem;
    font-size: 1.4rem;
  }
`;

export default TimelineNew;